/**
 * URL query-string serialization helpers for PurchaseOrderList.vue.
 *
 * Pure functions — no Vue/router dependency — so they can be unit-tested
 * with the Simple Component Pattern without mounting the giant list view.
 *
 * US-050: persists search, filters, pagination, and sort state into the
 * route query string so browser back/reload/bookmark/share all survive
 * navigation to a PO detail page and back. App.vue already mirrors Vue
 * Router's route into Shopify App Bridge's top-level URL, so anything
 * here lands in both the iframe URL and the embedded-app address bar
 * automatically.
 */

/**
 * Scope values the search-field dropdown accepts. Passed through to the
 * backend as-is; the backend `PO_SEARCH_FIELD_MAP` resolves each to the
 * actual Postgres column reference (see assisty-bot helper).
 */
export const PO_SEARCH_FIELD_VALUES = [
  'poNumber',
  'sku',
  'supplierSku',
  'productName',
  'variant',
  'supplier',
] as const;
export type PoSearchFieldValue = typeof PO_SEARCH_FIELD_VALUES[number];

/** Human labels for the scope dropdown. Exhaustive on PoSearchFieldValue. */
export const PO_SEARCH_FIELD_LABELS: Record<PoSearchFieldValue, string> = {
  poNumber:    'PO Number',
  sku:         'SKU',
  supplierSku: 'Supplier SKU',
  productName: 'Product Name',
  variant:     'Variant',
  supplier:    'Supplier',
};

const PO_SEARCH_FIELD_SET: ReadonlySet<string> = new Set(PO_SEARCH_FIELD_VALUES);

export interface PoListQueryState {
  search: string;
  searchField: PoSearchFieldValue | '';
  status: string[];
  source: string;
  supplierId: number | null;
  locationId: number | null;
  orderDateFrom: string;
  orderDateTo: string;
  departureDateFrom: string;
  departureDateTo: string;
  expectedDeliveryDateFrom: string;
  expectedDeliveryDateTo: string;
  paymentStatus: string[];
  overdue: boolean;
  page: number;
  itemsPerPage: number;
  sortBy: string;
  sortDesc: boolean;
}

/** Payment status values accepted from the URL (matches backend filter). */
export const PO_PAYMENT_STATUS_VALUES = ['unpaid', 'partial', 'paid'] as const;
const PO_PAYMENT_STATUS_SET: ReadonlySet<string> = new Set(PO_PAYMENT_STATUS_VALUES);

/**
 * PO status values accepted from the URL, in display order (matches
 * PurchaseOrders.getStatusOptions). Also the canonical order for the CSV
 * in both the URL and the API request.
 */
export const PO_STATUS_VALUES = [
  'draft',
  'confirmed',
  'partial_received',
  'received',
  'completed',
  'canceled',
] as const;
const PO_STATUS_SET: ReadonlySet<string> = new Set(PO_STATUS_VALUES);

/**
 * Quick-pick status groups. These used to be single-select filter values,
 * so `?status=open` / `?status=expecting` bookmarks still expand to them.
 */
export const PO_STATUS_PRESETS: ReadonlyArray<{
  value: string;
  text: string;
  statuses: ReadonlyArray<string>;
}> = [
  { value: 'open', text: 'Open Orders', statuses: ['draft', 'confirmed', 'partial_received'] },
  { value: 'expecting', text: 'Expected Deliveries', statuses: ['confirmed', 'partial_received'] },
];

/**
 * Expand presets, drop unknown values, dedupe, and sort canonically, so
 * the same set of statuses always serializes to the same CSV.
 */
export const normalizePoStatuses = (values: ReadonlyArray<string> | null | undefined): string[] => {
  const picked = new Set<string>();
  for (const raw of values || []) {
    const value = String(raw).trim();
    const preset = PO_STATUS_PRESETS.find((p) => p.value === value);
    if (preset) {
      preset.statuses.forEach((s) => picked.add(s));
    } else if (PO_STATUS_SET.has(value)) {
      picked.add(value);
    }
  }
  return PO_STATUS_VALUES.filter((s) => picked.has(s));
};

/** The preset whose statuses exactly match the selection, if any. */
export const findPoStatusPreset = (statuses: ReadonlyArray<string> | null | undefined) => {
  const normalized = normalizePoStatuses(statuses);
  return PO_STATUS_PRESETS.find((p) =>
    p.statuses.length === normalized.length && p.statuses.every((s) => normalized.includes(s)),
  );
};

/**
 * Canonical URL of the bare PO list view. Used as the fallback target
 * for `resolveBackToListPath` when the user deep-linked into a detail
 * page or arrived from a route outside the PO list.
 */
export const PO_LIST_BASE_PATH = '/purchase-orders';

export const PO_LIST_DEFAULTS: Readonly<PoListQueryState> = Object.freeze({
  search: '',
  searchField: '',
  status: [],
  source: '',
  supplierId: null,
  locationId: null,
  orderDateFrom: '',
  orderDateTo: '',
  departureDateFrom: '',
  departureDateTo: '',
  expectedDeliveryDateFrom: '',
  expectedDeliveryDateTo: '',
  paymentStatus: [],
  overdue: false,
  page: 1,
  itemsPerPage: 10,
  sortBy: 'orderDate',
  sortDesc: true,
});

type RawQueryValue = string | (string | null)[] | null | undefined;
type RawQuery = Record<string, RawQueryValue>;

const STRING_KEYS: Array<keyof PoListQueryState> = [
  'search',
  'source',
  'orderDateFrom',
  'orderDateTo',
  'departureDateFrom',
  'departureDateTo',
  'expectedDeliveryDateFrom',
  'expectedDeliveryDateTo',
  'sortBy',
];

const firstString = (value: RawQueryValue): string | undefined => {
  if (value === null || value === undefined) { return undefined; }
  if (Array.isArray(value)) {
    const first = value[0];
    return first === null || first === undefined ? undefined : first;
  }
  return value;
};

const parsePositiveInt = (
  raw: string | undefined,
  max?: number,
): number | undefined => {
  if (raw === undefined || raw === '') { return undefined; }
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 1) { return undefined; }
  // Clamp (not drop) — respect user intent when possible while stopping
  // stale bookmarks like `?itemsPerPage=999999` from triggering
  // oversized backend fetches that LIMIT forwards into the SQL query.
  if (max !== undefined && parsed > max) { return max; }
  return parsed;
};

/** Maximum page size accepted from the URL. Matches typical Vuetify page-size options. */
const MAX_ITEMS_PER_PAGE = 100;

const parseStrictBoolean = (raw: string | undefined): boolean | undefined => {
  if (raw === 'true') { return true; }
  if (raw === 'false') { return false; }
  return undefined;
};

/**
 * Parse a Vue Router query object into a partial PoListQueryState.
 * Only keys that are present AND valid appear in the output — invalid
 * and unknown keys are silently dropped so bookmarks with stale keys
 * don't break the page.
 */
export const parsePoListQuery = (query: RawQuery): Partial<PoListQueryState> => {
  const out: Partial<PoListQueryState> = {};

  for (const key of STRING_KEYS) {
    const raw = firstString(query[key]);
    if (raw !== undefined && raw !== '') {
      (out as Record<string, unknown>)[key] = raw;
    }
  }

  // Whitelist against PO_SEARCH_FIELD_SET — stale bookmarks from a
  // renamed/removed scope value must not leak into component state.
  const rawSearchField = firstString(query.searchField);
  if (rawSearchField !== undefined && PO_SEARCH_FIELD_SET.has(rawSearchField)) {
    out.searchField = rawSearchField as PoSearchFieldValue;
  }

  const supplierId = parsePositiveInt(firstString(query.supplierId));
  if (supplierId !== undefined) { out.supplierId = supplierId; }

  const locationId = parsePositiveInt(firstString(query.locationId));
  if (locationId !== undefined) { out.locationId = locationId; }

  const page = parsePositiveInt(firstString(query.page));
  if (page !== undefined) { out.page = page; }

  const itemsPerPage = parsePositiveInt(firstString(query.itemsPerPage), MAX_ITEMS_PER_PAGE);
  if (itemsPerPage !== undefined) { out.itemsPerPage = itemsPerPage; }

  const sortDesc = parseStrictBoolean(firstString(query.sortDesc));
  if (sortDesc !== undefined) { out.sortDesc = sortDesc; }

  // paymentStatus: CSV in the URL → whitelisted array. Unknown values dropped.
  const rawPaymentStatus = firstString(query.paymentStatus);
  if (rawPaymentStatus !== undefined && rawPaymentStatus !== '') {
    const values = Array.from(new Set(
      rawPaymentStatus.split(',').map((s) => s.trim()).filter((s) => PO_PAYMENT_STATUS_SET.has(s)),
    ));
    if (values.length) { out.paymentStatus = values; }
  }

  // status: CSV in the URL → canonical array. Legacy preset values expand.
  const rawStatus = firstString(query.status);
  if (rawStatus !== undefined && rawStatus !== '') {
    const statuses = normalizePoStatuses(rawStatus.split(','));
    if (statuses.length) { out.status = statuses; }
  }

  const overdue = parseStrictBoolean(firstString(query.overdue));
  if (overdue !== undefined) { out.overdue = overdue; }

  return out;
};

/**
 * Serialize state into a query object, omitting any field equal to its
 * default. An all-default state serializes to {} — so the URL for the
 * unfiltered list is exactly `/purchase-orders` with no query string.
 */
export const serializePoListState = (
  state: PoListQueryState
): Record<string, string> => {
  const out: Record<string, string> = {};

  for (const key of STRING_KEYS) {
    // Defensively allow null/undefined. The interface types these as
    // `string`, but Vuetify 2's VTextField `clearable` × button runs
    // `clearableCallback` → sets `internalValue = null` and emits
    // `@input` with null, which flows through v-model into component
    // data. Treating null/undefined as "no value" (i.e. same as the
    // default) stops a cleared input from leaking `?search` into the
    // URL. Parse side already handles null via `firstString`, so this
    // keeps parse/serialize symmetrical.
    const value = state[key] as string | null | undefined;
    if (value == null) { continue; }
    if (value !== PO_LIST_DEFAULTS[key]) {
      out[key] = value;
    }
  }

  // Handled outside STRING_KEYS because the parser whitelists this key
  // against PO_SEARCH_FIELD_SET; the branch is kept explicit so serialize
  // mirrors parse and the union type stays intact.
  if (state.searchField !== PO_LIST_DEFAULTS.searchField) {
    out.searchField = state.searchField;
  }

  if (state.supplierId !== null && state.supplierId !== PO_LIST_DEFAULTS.supplierId) {
    out.supplierId = String(state.supplierId);
  }
  if (state.locationId !== null && state.locationId !== PO_LIST_DEFAULTS.locationId) {
    out.locationId = String(state.locationId);
  }
  if (state.page !== PO_LIST_DEFAULTS.page) {
    out.page = String(state.page);
  }
  if (state.itemsPerPage !== PO_LIST_DEFAULTS.itemsPerPage) {
    out.itemsPerPage = String(state.itemsPerPage);
  }
  if (state.sortDesc !== PO_LIST_DEFAULTS.sortDesc) {
    out.sortDesc = String(state.sortDesc);
  }
  if (Array.isArray(state.paymentStatus) && state.paymentStatus.length > 0) {
    out.paymentStatus = state.paymentStatus.join(',');
  }
  const statuses = normalizePoStatuses(state.status);
  if (statuses.length > 0) {
    out.status = statuses.join(',');
  }
  if (state.overdue) {
    out.overdue = String(state.overdue);
  }

  return out;
};

/**
 * Given a Vue Router "from" route captured in `beforeRouteEnter` on
 * `PurchaseOrderDetail.vue`, return the path the detail page should
 * navigate to when the user clicks its "back to list" button.
 *
 *  - If the user arrived from the PO list, return from.fullPath so
 *    the query string (filter + pagination + sort state) is preserved.
 *  - In every other case — deep-linked URL, arrived from a different
 *    page (dashboard, supplier, etc.), arrived from a sub-path like
 *    /purchase-orders/123 (PO → edit PO → back to PO), or the caller
 *    couldn't capture a fullPath — fall back to the bare list URL.
 *
 * The exact-match on `fromPath === PO_LIST_BASE_PATH` is intentional:
 * `/purchase-orders/123` should NOT match, because "back" from an
 * intermediate detail page should land on the clean list, not the
 * previous detail page.
 */
export const resolveBackToListPath = (
  fromPath: string | null | undefined,
  fromFullPath: string | null | undefined
): string => {
  if (fromPath === PO_LIST_BASE_PATH && fromFullPath) {
    return fromFullPath;
  }
  return PO_LIST_BASE_PATH;
};

/**
 * Shallow-equal two Vue Router query objects. Used before
 * `router.replace` to avoid `NavigationDuplicated` errors on no-op
 * updates. Array values are compared by identity, not element-wise —
 * we never emit arrays from `serializePoListState`, so this is safe.
 */
export const queryShallowEqual = (a: RawQuery, b: RawQuery): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) { return false; }
  for (const key of aKeys) {
    if (a[key] !== b[key]) { return false; }
  }
  return true;
};
